/**
 * Accept a workspace invite via token link (iter-78).
 * Flow:
 *  1. Fetch invite info (email, role, workspace name) without auth
 *  2. If logged in with the invited email → accept immediately
 *  3. If not logged in → redirect to login/register with return URL
 *  4. If logged in with different email → show mismatch error
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/workspaces/invite-info/${token}`);
        if (!r.ok) throw new Error((await r.json()).detail || "Invite not found");
        setInfo(await r.json());
      } catch (err) { setError(err.message || "Invite not found"); }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post("/workspaces/accept-invite", { token });
      toast.success(`Joined ${info.workspace_name}`);
      setDone(true);
      setTimeout(() => navigate("/library/workspaces"), 1200);
      return data;
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAccepting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#f6f4ee] flex items-center justify-center p-6">
        <Toaster position="bottom-right" richColors />
        <div className="max-w-md bg-white border border-[color:var(--border)] p-8" data-testid="accept-invite-error">
          <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">§ Team invite</p>
          <h1 className="font-editorial text-3xl mt-3">Invite not available</h1>
          <p className="text-sm text-[color:var(--text-muted)] mt-3">{error}</p>
          <p className="text-sm text-[color:var(--text-muted)] mt-3">
            <Link to="/login" className="underline">Log in</Link> or{" "}
            <a href="mailto:support@bookforge360.com" className="underline">contact support</a>.
          </p>
        </div>
      </div>
    );
  }
  if (loading || !info) {
    return (
      <div className="min-h-screen bg-[#f6f4ee] flex items-center justify-center">
        <p className="text-sm text-[color:var(--text-muted)]">Loading invite…</p>
      </div>
    );
  }

  const roleLabel = { admin: "Admin", editor: "Editor", viewer: "Viewer" }[info.role] || "Editor";
  const emailMatch = user && (user.email || "").toLowerCase() === (info.email || "").toLowerCase();

  return (
    <div className="min-h-screen bg-[#f6f4ee] flex items-center justify-center p-6" data-testid="accept-invite-page">
      <Toaster position="bottom-right" richColors />
      <div className="max-w-md w-full bg-white border border-[color:var(--border)] p-8">
        <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">§ Team invite</p>
        <h1 className="font-editorial text-3xl mt-3">You've been invited</h1>
        <p className="text-sm mt-4">
          You've been invited to join <strong>{info.workspace_name}</strong> on{" "}
          <strong>BookForge360</strong> as <strong>{roleLabel}</strong>.
        </p>
        <p className="text-xs text-[color:var(--text-muted)] mt-2">
          Invite addressed to <span className="font-mono">{info.email}</span>
        </p>

        {!user && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-[color:var(--text-muted)]">
              Log in or create an account with <strong>{info.email}</strong> to accept.
            </p>
            <div className="flex gap-2">
              <Link to={`/login?next=/accept-invite/${token}`}
                     className="btn-primary text-sm flex-1 text-center"
                     data-testid="accept-invite-login">Log in</Link>
              <Link to={`/register?next=/accept-invite/${token}&email=${encodeURIComponent(info.email)}`}
                     className="btn-ghost text-sm flex-1 text-center"
                     data-testid="accept-invite-register">Create account</Link>
            </div>
          </div>
        )}

        {user && !emailMatch && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-red-700" data-testid="accept-invite-mismatch">
              You're logged in as <span className="font-mono">{user.email}</span>, but this
              invite is for <span className="font-mono">{info.email}</span>. Log out and log
              back in with the correct email to accept.
            </p>
            <Link to="/login" className="btn-primary text-sm inline-block">Switch account</Link>
          </div>
        )}

        {user && emailMatch && !done && (
          <div className="mt-6">
            <button onClick={accept} disabled={accepting}
                    className="btn-primary text-sm w-full"
                    data-testid="accept-invite-confirm">
              {accepting ? "Joining…" : `Join ${info.workspace_name}`}
            </button>
          </div>
        )}

        {done && (
          <p className="text-sm text-green-700 mt-6" data-testid="accept-invite-success">
            ✓ Joined. Redirecting to your workspaces…
          </p>
        )}
      </div>
    </div>
  );
}
